'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AlertTriangle, Info, Megaphone, X } from 'lucide-react';
import Link from '@/core/i18n/navigation';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import { cn } from '@/core/utils/cn';
import { useHeaderTransparency } from '@/core/components/providers/HeaderTransparencyContext';

/** Stored shape (backend schemas.config.AlertBarConfig) — saved without defaults, applied here. */
export interface AlertBarConfig {
  enabled?: boolean;
  message?: string;
  level?: 'info' | 'warning' | 'urgent';
  link_url?: string | null;
  link_label?: string | null;
  dismissible?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  translations?: Record<string, { message?: string | null; link_label?: string | null }> | null;
}

const LEVEL_STYLE = {
  info: { className: 'bg-slate-900 text-white', Icon: Info },
  warning: { className: 'bg-amber-400 text-amber-950', Icon: Megaphone },
  urgent: { className: 'bg-red-700 text-white', Icon: AlertTriangle },
} as const;

const DISMISS_KEY = 'stag-alert-dismissed';

export function isAlertActive(config: AlertBarConfig | null | undefined, now: number = Date.now()): boolean {
  if (!config?.enabled || !config.message?.trim()) return false;
  if (config.starts_at && Date.parse(config.starts_at) > now) return false;
  if (config.ends_at && Date.parse(config.ends_at) <= now) return false;
  return true;
}

/** Changes whenever the message or its schedule changes, so an edited alert shows again. */
function alertFingerprint(config: AlertBarConfig): string {
  const source = `${config.message}|${config.starts_at || ''}|${config.level || ''}`;
  let hash = 0;
  for (let i = 0; i < source.length; i++) hash = (hash * 31 + source.charCodeAt(i)) | 0;
  return String(hash);
}

/**
 * "Pasek komunikatu" above the storefront header (Wygląd → Pasek komunikatu). Shown in website
 * and shop mode alike — e.g. "przerwa świąteczna w wysyłkach", "szkoła zamknięta 2 maja".
 */
export function AlertBar({ config }: { config: AlertBarConfig | null | undefined }) {
  const { t, locale } = useTranslation();
  const [hidden, setHidden] = useState(false);
  // Over a transparent hero the header is `fixed top-0`; an in-flow bar would sit underneath it.
  // The bar then pins itself above the header and publishes its height (--alert-bar-h), which
  // both header shells use as their `top`.
  const pinned = useHeaderTransparency().enabled;
  const barRef = useRef<HTMLDivElement | null>(null);
  const visible = !!config && !hidden && isAlertActive(config);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const node = barRef.current;
    if (!pinned || !visible || !node) {
      root.style.removeProperty('--alert-bar-h');
      return;
    }
    const publish = () => root.style.setProperty('--alert-bar-h', `${node.offsetHeight}px`);
    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    observer?.observe(node);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--alert-bar-h');
    };
  }, [pinned, visible]);

  // The schedule and the visitor's "close" are re-checked after hydration: the server copy may
  // be cached past ends_at, and the dismissal lives in this browser only.
  useEffect(() => {
    if (!config) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === alertFingerprint(config);
    } catch { /* storage unavailable — keep showing */ }
    if (!isAlertActive(config) || (config.dismissible !== false && dismissed)) setHidden(true);
  }, [config]);

  if (!config || hidden || !isAlertActive(config)) return null;

  const translated = config.translations?.[locale];
  const message = translated?.message?.trim() || config.message!;
  const linkLabel = translated?.link_label?.trim() || config.link_label?.trim() || t('alert_bar.more', 'Więcej');
  const level = config.level && config.level in LEVEL_STYLE ? config.level : 'info';
  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, alertFingerprint(config));
    } catch { /* storage unavailable — hide for this page view only */ }
    setHidden(true);
  };

  const view = (
    <AlertBarView
      message={message}
      level={level}
      linkUrl={config.link_url || null}
      linkLabel={linkLabel}
      onDismiss={config.dismissible !== false ? dismiss : undefined}
      closeLabel={t('alert_bar.close', 'Zamknij komunikat')}
    />
  );
  return pinned ? <div ref={barRef} className="fixed inset-x-0 top-0 z-40">{view}</div> : view;
}

/** Presentational part — also the live preview in Wygląd → Pasek komunikatu. */
export function AlertBarView({ message, level, linkUrl, linkLabel, onDismiss, closeLabel }: {
  message: string;
  level: keyof typeof LEVEL_STYLE;
  linkUrl: string | null;
  linkLabel: string;
  onDismiss?: () => void;
  closeLabel: string;
}) {
  const { className, Icon } = LEVEL_STYLE[level] || LEVEL_STYLE.info;
  const external = !!linkUrl && !linkUrl.startsWith('/');
  return (
    <div role={level === 'urgent' ? 'alert' : 'status'} className={cn('w-full text-sm', className)}>
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 md:px-8">
        <Icon className="h-4 w-4 shrink-0" aria-hidden />
        <p className="flex-1 leading-snug">
          {message}
          {linkUrl && (
            <>
              {' '}
              {external ? (
                <a href={linkUrl} className="font-semibold underline underline-offset-2" rel="noopener noreferrer">{linkLabel}</a>
              ) : (
                <Link href={linkUrl} className="font-semibold underline underline-offset-2">{linkLabel}</Link>
              )}
            </>
          )}
        </p>
        {onDismiss && (
          <button type="button" onClick={onDismiss} className="-mr-1 rounded p-1 opacity-80 transition hover:opacity-100 focus-visible:outline focus-visible:outline-2" aria-label={closeLabel}>
            <X className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
    </div>
  );
}
